import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowDownRight } from "lucide-react";
import { BRAND, IMAGES, whatsappLink } from "@/config";

const words = ["ZUVERLÄSSIG.", "SAUBER.", "EINSATZBEREIT."];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.65, 0.95]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Premium Fahrzeug bei Nacht"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-black"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 to-transparent" />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 lg:px-10 pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#0052ff]" />
          <span className="text-xs uppercase tracking-[0.35em] text-[#94a3b8]">
            {BRAND.full}
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold leading-[0.95] tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl">
          {words.map((w, i) => (
            <span key={w} className="block overflow-hidden py-0.5">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 1 ? (
                  <span className="text-[#0052ff]">{w}</span>
                ) : (
                  w
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#94a3b8] leading-relaxed"
        >
          Fahrdienste, Überführungen, Reinigung & Pflege sowie Hol- und
          Bringservice für Ihr Fahrzeug — norddeutsche Präzision, deutschlandweit
          im Einsatz.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03]"
          >
            <FaWhatsapp className="text-lg" />
            Direkt via WhatsApp
          </a>
          <a
            href="#leistungen"
            data-testid="hero-services-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:border-[#0052ff] hover:text-[#0052ff]"
          >
            Unsere Leistungen
            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
